import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  Snackbar,
  IconButton,
  InputAdornment,
  CssBaseline
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Settings as SettingsIcon,
  LocationOn as LocationIcon,
  Flight as FlightIcon,
  Help as HelpIcon,
  Warning as WarningIcon,
  Lock as LockIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [loginId, setLoginId] = useState('');
  const [step, setStep] = useState('credentials'); // 'credentials' | 'otp'
  const [showPassword, setShowPassword] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState('success');
  const [captchaText, setCaptchaText] = useState('');
  const [captchaShapes, setCaptchaShapes] = useState([]);
  const [captchaInput, setCaptchaInput] = useState('');
  const { startLogin, verifyOtp } = useAuth();
  const navigate = useNavigate();

  const generateCaptcha = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let str = '';
    for (let i = 0; i < 6; i++) {
      str += chars[Math.floor(Math.random() * chars.length)];
    }
    setCaptchaText(str);
    const palette = ['#1e3a8a', '#7c3aed', '#2563eb', '#ef4444', '#f59e0b'];
    const shapes = Array.from({ length: 6 }).map(() => ({
      rotate: Math.floor(-25 + Math.random() * 50),
      dx: Math.floor(-3 + Math.random() * 6),
      dy: Math.floor(-3 + Math.random() * 6),
      color: palette[Math.floor(Math.random() * palette.length)],
    }));
    setCaptchaShapes(shapes);
    setCaptchaInput('');
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('logoutReason');
      if (stored && stored.trim()) {
        setToastSeverity('info');
        setToastMessage(stored);
        setToastOpen(true);
      }
      localStorage.removeItem('logoutReason');
    } catch {
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step === 'otp') {
      try {
        await verifyOtp(loginId, otp);
        setToastSeverity('success');
        setToastMessage('OTP verified. Login successful');
        setToastOpen(true);
        setTimeout(() => navigate('/dashboard'), 600);
      } catch (err) {
        setToastSeverity('error');
        setToastMessage(err.message || 'OTP verification failed');
        setToastOpen(true);
      }
      return;
    }

    if (captchaInput.trim().toUpperCase() !== captchaText) {
      setToastSeverity('error');
      setToastMessage('Invalid captcha');
      setToastOpen(true);
      return;
    }
    try {
      const result = await startLogin(email, password);
      if (result?.otpRequired) {
        setLoginId(result.loginId);
        setStep('otp');
        setOtp('');
        setToastSeverity('success');
        setToastMessage(result.message || 'OTP sent to your email');
        setToastOpen(true);
        return;
      }

      // fallback (if token returned directly)
      setToastSeverity('success');
      setToastMessage('Login successful');
      setToastOpen(true);
      setTimeout(() => navigate('/dashboard'), 600);
    } catch (err) {
      setToastSeverity('error');
      setToastMessage(err.message || 'Login failed');
      setToastOpen(true);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f8f9fa',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <CssBaseline />

      {/* Background Dashed Line (SVG) */}
      <Box
        component="svg"
        viewBox="0 0 1440 800"
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          height: '100%',
          zIndex: 0,
          opacity: 0.6,
          pointerEvents: 'none'
        }}
      >
        <path
          d="M 100,200 Q 400,100 720,400 T 1340,600"
          fill="none"
          stroke="#3f51b5"
          strokeWidth="2"
          strokeDasharray="10,10"
        />
        <path
          d="M 200,600 Q 500,700 800,500 T 1200,200"
          fill="none"
          stroke="#3f51b5"
          strokeWidth="2"
          strokeDasharray="10,10"
        />
      </Box>

      {/* Background Icons */}
      <SettingsIcon sx={{ position: 'absolute', top: '15%', left: '20%', color: '#64b5f6', fontSize: 60, opacity: 0.8, animation: 'spin 10s linear infinite' }} />
      <FlightIcon sx={{ position: 'absolute', top: '10%', left: '50%', color: '#1976d2', fontSize: 40, transform: 'rotate(45deg)' }} />
      <LocationIcon sx={{ position: 'absolute', top: '15%', right: '25%', color: '#ffa726', fontSize: 70 }} />
      <HelpIcon sx={{ position: 'absolute', top: '40%', right: '15%', color: '#ef5350', fontSize: 50 }} />
      <WarningIcon sx={{ position: 'absolute', top: '55%', left: '15%', color: '#e57373', fontSize: 50 }} />
      <LockIcon sx={{ position: 'absolute', bottom: '15%', left: '30%', color: '#ffb74d', fontSize: 60 }} />

      {/* Main Login Card */}
      <Paper
        elevation={4}
        sx={{
          zIndex: 2,
          p: 5,
          width: '100%',
          maxWidth: '450px',
          borderRadius: '24px',
          textAlign: 'center',
          bgcolor: 'white'
        }}
      >
        <Typography variant="h4" fontWeight="700" gutterBottom sx={{ color: '#2c3e50' }}>
          Welcome back!
        </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please enter your details to sign in
          </Typography>
 
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            margin="normal"
            fullWidth
            id="email"
            label="Email"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={step === 'otp'}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                bgcolor: '#f5f6fa'
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e0e0e0'
              }
            }}
          />
          <TextField
            margin="normal"
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={step === 'otp'}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                bgcolor: '#f5f6fa'
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e0e0e0'
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {step === 'credentials' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
              <Box sx={{ px: 2, py: 1, borderRadius: '10px', bgcolor: '#e3f2fd' }}>
                <Box
                  component="svg"
                  viewBox="0 0 180 50"
                  sx={{ width: 180, height: 50 }}
                >
                  <defs>
                    <filter id="noise">
                      <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="1" result="noisy" />
                      <feDisplacementMap in="SourceGraphic" in2="noisy" scale="1" />
                    </filter>
                  </defs>
                  {captchaText.split('').map((ch, i) => {
                    const x = 15 + i * 28 + (captchaShapes[i]?.dx || 0);
                    const y = 32 + (captchaShapes[i]?.dy || 0);
                    const rot = captchaShapes[i]?.rotate || 0;
                    const color = captchaShapes[i]?.color || '#1e3a8a';
                    return (
                      <text
                        key={i}
                        x={x}
                        y={y}
                        fontSize="26"
                        fontWeight="700"
                        fill={color}
                        transform={`rotate(${rot} ${x} ${y})`}
                        filter="url(#noise)"
                      >
                        {ch}
                      </text>
                    );
                  })}
                  <line x1="10" y1="12" x2="170" y2="10" stroke="#93c5fd" strokeWidth="2" />
                  <line x1="10" y1="40" x2="170" y2="42" stroke="#c4b5fd" strokeWidth="2" />
                </Box>
              </Box>
              <IconButton onClick={generateCaptcha} color="primary" aria-label="refresh captcha">
                <RefreshIcon />
              </IconButton>
              <TextField
                label="Captcha"
                value={captchaInput}
                onChange={(e) => setCaptchaInput(e.target.value)}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    bgcolor: '#f5f6fa'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e0e0e0'
                  }
                }}
              />
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    bgcolor: '#f5f6fa'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e0e0e0'
                  }
                }}
              />
              <Typography
                variant="body2"
                sx={{ mt: 1.25, color: 'text.secondary', display: 'flex', justifyContent: 'space-between' }}
              >
                <span>OTP sent to your registered email.</span>
                <span
                  style={{ cursor: 'pointer', color: '#2563eb', fontWeight: 600 }}
                  onClick={async () => {
                    try {
                      const result = await startLogin(email, password);
                      if (result?.otpRequired) {
                        setLoginId(result.loginId);
                        setToastSeverity('success');
                        setToastMessage('OTP resent');
                        setToastOpen(true);
                      }
                    } catch (err) {
                      setToastSeverity('error');
                      setToastMessage(err.message || 'Failed to resend OTP');
                      setToastOpen(true);
                    }
                  }}
                >
                  Resend OTP
                </span>
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, mb: 3 }}>
            <Typography
              variant="body2"
              onClick={() => navigate('/forgot-password')}
              sx={{
                color: '#ffb74d',
                cursor: 'pointer',
                fontWeight: 600,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Forgot Password?
            </Typography>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              py: 1.5,
              borderRadius: '12px',
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              bgcolor: '#42a5f5',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#2196f3',
                boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)'
              }
            }}
          >
            {step === 'otp' ? 'Verify OTP' : 'Sign In'}
          </Button>
        </Box>
      </Paper>
 
      {/* Footer Links */}
      <Box sx={{ position: 'absolute', bottom: 20, right: 30, display: 'flex', gap: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ cursor: 'pointer' }}>Privacy Settings</Typography>
          <Typography variant="caption" color="text.secondary">•</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ cursor: 'pointer' }}>Terms & Conditions</Typography>
      </Box>
 
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={toastOpen}
        autoHideDuration={2500}
        onClose={() => setToastOpen(false)}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity={toastSeverity}
          sx={{ width: '100%' }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
 
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
          100% { transform: translateY(0px); }
        }
      `}</style>
    </Box>
  );
}
